import { Children, useMemo, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Linking,
  Image,
} from "react-native";
import {
  ChevronLeft,
  FileText,
  Globe,
  Info,
  MessageCircle,
  Trophy,
  MapPin,
  Users,
  Shield,
  Heart,
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import type { NavigationProp } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useOnboarding } from "../../../onboarding/OnboardingContext";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { EMBER } from "../../../onboarding/theme";
import type { CostsStackParamList, MainTabParamList } from "../../types";
import { useRegion, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import RegionGatePlaceholder from "../../components/RegionGatePlaceholder";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  CardSubtitleText,
  CardTitleText,
  IconButton,
  MoonDetailRow,
  MoonEmptyState,
  SectionTitle,
  SoftSecondaryButton,
  StatusBadge,
  Tag,
  VelvetPrimaryButton,
} from "@louise/moon-ui";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import EligibilityBadge from "./EligibilityBadge";
import { GRANTS } from "./grants.data";
import type { Grant } from "./grants.types";
import { checkGrantEligibility } from "./grants.utils";
import { useSmartBack } from "../../hooks/useSmartBack";
import { inferRegionCode } from "@src/packages/costs/estimates/locations";

type Props = NativeStackScreenProps<CostsStackParamList, "GrantDetail">;

const GRANT_NOT_FOUND_ILLUSTRATION =
  require("@src/apps/moon/assets/images/grant-not-found-illustration.png") as number;

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <View style={{ gap: 10 }}>
      <SectionTitle icon={icon}>{title}</SectionTitle>
      {children}
    </View>
  );
}

function InsetGroup({ children }: { children: React.ReactNode }) {
  return (
    <View
      style={{
        padding: 16,
        borderRadius: 16,
        backgroundColor: "rgba(255, 255, 255, 0.28)",
        borderWidth: 1,
        borderColor: "rgba(255, 255, 255, 0.56)",
      }}
    >
      {children}
    </View>
  );
}

function DetailGrid({
  children,
  minColumnWidth,
}: {
  children: React.ReactNode;
  minColumnWidth: number;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 14,
      }}
    >
      {Children.map(children, (child, index) =>
        child ? (
          <View
            key={index}
            style={{
              flexGrow: 1,
              flexBasis: minColumnWidth,
              minWidth: minColumnWidth,
            }}
          >
            {child}
          </View>
        ) : null,
      )}
    </View>
  );
}

function TagList({ items }: { items: string[] }) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
      {items.map((item) => (
        <Tag key={item} label={item} tone="neutral" shape="pill" />
      ))}
    </View>
  );
}

function buildGrantChatPrompt(grant: Grant) {
  return [
    "I want help understanding whether this fertility grant may be a fit and how to prepare an application. Ask clarifying questions before making assumptions about my eligibility, finances, insurance, or medical history.",
    `Grant: ${grant.name} (${grant.organization})`,
    `Award: ${grant.awardAmount}`,
    `Treatments covered: ${grant.treatmentsCovered.join(", ")}`,
    `How to apply: ${grant.howToApply}`,
    `Listed requirements: ${grant.requirements.join("; ")}`,
  ].join("\n");
}

function GrantsBackLink({
  onPress,
  label,
}: {
  onPress: () => void;
  label: string;
}) {
  const capturePress = usePressCapture("GrantsBackLink");
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={() => {
        capturePress(pressLabel(label), "back");
        onPress();
      }}
      hitSlop={8}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        marginBottom: 8,
      }}
    >
      <ChevronLeft size={18} color={EMBER.colors.warm} strokeWidth={1.5} />
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.warm }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

export default function GrantDetailScreen({ navigation, route }: Props) {
  const region = useRegion();
  const t = useTranslations();
  const { grantId, returnTab } = route.params;
  const backLabel = returnTab ? t("you.shared.back") : t("costs.grants");
  const grant = useMemo(
    () => GRANTS.find((g) => g.id === grantId) ?? null,
    [grantId],
  );

  const { data: onboarding } = useOnboarding();
  const { data: patient } = usePatient();
  const coverage = useCoverage();
  const [failedFavicon, setFailedFavicon] = useState<string | null>(null);
  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "Grants",
  });

  const eligibility = useMemo(() => {
    if (!grant) return null;
    return checkGrantEligibility(grant, {
      state: inferRegionCode(
        patient?.addresses?.[0]?.country ?? onboarding?.country,
        patient?.addresses?.[0]?.postalCode ?? onboarding?.zipCode,
      ),
      age: onboarding?.age ?? null,
      hasFertilityCoverage: coverage?.fertilityCoverage?.hasCoverage ?? null,
    });
  }, [
    grant,
    onboarding?.country,
    onboarding?.zipCode,
    onboarding?.age,
    patient?.addresses,
    coverage?.fertilityCoverage?.hasCoverage,
  ]);

  if (!grant) {
    return (
      <GlassScreenLayout>
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingHorizontal: 16,
            paddingTop: SCREEN_PADDING_TOP,
            paddingBottom: 100,
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
          }}
          showsVerticalScrollIndicator={false}
        >
          <GrantsBackLink onPress={handleBack} label={backLabel} />
          <View
            style={{
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MoonEmptyState
              illustration={
                <Image
                  source={GRANT_NOT_FOUND_ILLUSTRATION}
                  accessible={false}
                  accessibilityIgnoresInvertColors
                  resizeMode="contain"
                  style={{ width: 128, height: 128 }}
                />
              }
              title="Grant not found"
              subtitle="We couldn't find that grant. Browse the grants list to see current opportunities."
              subtitleStyle={{
                color: EMBER.colors.textLight,
                fontSize: 16,
                lineHeight: 24,
                maxWidth: 320,
              }}
              action={
                <VelvetPrimaryButton
                  label="Back to grants"
                  fullWidth={false}
                  onPress={handleBack}
                />
              }
              style={{ width: "100%" }}
            />
          </View>
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  const statusConfig = {
    open: { label: "Open", tone: "green" as const, icon: CheckCircle },
    upcoming: { label: "Upcoming", tone: "gold" as const, icon: Clock },
    closed: { label: "Closed", tone: "neutral" as const, icon: Calendar },
  }[grant.applicationStatus];

  const locationLabel =
    grant.eligibility.location === "national"
      ? "United States (nationwide)"
      : grant.eligibility.location.join(", ");

  const ageLabel =
    grant.eligibility.ageMin != null && grant.eligibility.ageMax != null
      ? `Ages ${grant.eligibility.ageMin}–${grant.eligibility.ageMax}`
      : grant.eligibility.ageMax != null
        ? `Under ${grant.eligibility.ageMax}`
        : grant.eligibility.ageMin != null
          ? `${grant.eligibility.ageMin}+`
          : null;

  const insuranceLabel = {
    any: "Any insurance status",
    no_fertility: "No fertility insurance coverage",
    none_required: "No insurance required",
  }[grant.eligibility.insuranceRequired];

  const citizenshipLabel = {
    us_citizen: "U.S. citizen",
    us_resident: "U.S. citizen or permanent resident",
    us_or_canada: "U.S. or Canadian resident",
  }[grant.eligibility.citizenshipRequired];

  const maritalLabel =
    grant.eligibility.maritalStatus === "married" ? "Legally married" : null;

  const diagnosisLabel = {
    infertility: "Infertility diagnosis required",
    cancer: "Cancer diagnosis required",
    cystic_fibrosis: "Cystic fibrosis diagnosis required",
    endometriosis: "Endometriosis diagnosis required",
    brca: "BRCA1 or BRCA2 mutation required",
    any: null,
  }[grant.eligibility.diagnosis];

  const specialGroupLabel = grant.eligibility.specialGroups
    ? {
        veterans: "Wounded veterans",
        lgbtq: "LGBTQ+ individuals",
        jewish: "Jewish community",
      }[grant.eligibility.specialGroups]
    : null;

  if (region !== "US") return <RegionGatePlaceholder />;

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <GrantsBackLink onPress={handleBack} label={backLabel} />

        {/* Main card */}
        <View
          style={{
            ...(GLASS_CARD_STYLE as object),
            paddingHorizontal: 24,
            paddingTop: 24,
            paddingBottom: 28,
            gap: 24,
          }}
        >
          {/* Header */}
          <View style={{ gap: 16, alignItems: "center" }}>
            {/* Status badges */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <StatusBadge
                label={statusConfig.label}
                tone={statusConfig.tone}
                icon={statusConfig.icon}
                shape="pill"
              />
              {eligibility && <EligibilityBadge status={eligibility} />}
            </View>

            {/* Logo, title, and organization */}
            <View
              style={{
                alignItems: "center",
                gap: 10,
                alignSelf: "stretch",
              }}
            >
              <View
                style={{
                  width: 48,
                  height: 48,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 14,
                  backgroundColor: "rgba(255, 255, 255, 0.42)",
                  borderWidth: 1,
                  borderColor: "rgba(255, 255, 255, 0.58)",
                }}
              >
                {failedFavicon === grant.favicon ? (
                  <Globe
                    size={18}
                    color={EMBER.colors.warm}
                    strokeWidth={1.8}
                  />
                ) : (
                  <Image
                    source={{ uri: grant.favicon }}
                    onError={() => setFailedFavicon(grant.favicon)}
                    style={
                      {
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        opacity: 0.65,
                        ...(Platform.OS === "web" && {
                          filter: "grayscale(1)",
                        }),
                      } as Record<string, unknown>
                    }
                  />
                )}
              </View>
              <CardTitleText
                style={
                  {
                    fontSize: 26,
                    lineHeight: 32,
                    fontWeight: "700",
                    color: "#3B1A08",
                    alignSelf: "stretch",
                    textAlign: "center",
                    ...(Platform.OS === "web" && {
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
                      backgroundSize: "200px 200px, 100% 100%",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }),
                  } as Record<string, unknown>
                }
              >
                {grant.name}
              </CardTitleText>
            </View>

            <CardSubtitleText
              style={{
                color: EMBER.colors.textFaint,
                marginTop: 0,
                textAlign: "center",
              }}
            >
              by {grant.organization}
            </CardSubtitleText>
          </View>

          {/* Award */}
          <InsetGroup>
            <View style={{ gap: 24 }}>
              <View style={{ gap: 10, alignItems: "center" }}>
                <View
                  style={{
                    alignSelf: "stretch",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 30,
                  }}
                >
                  <SectionTitle icon={Trophy}>Award</SectionTitle>
                  <IconButton
                    accessibilityLabel={`Visit ${grant.organization} website`}
                    size="sm"
                    surface="soft"
                    backgroundColor="rgba(255, 255, 255, 0.5)"
                    borderColor="rgba(255, 255, 255, 0.65)"
                    borderWidth={1}
                    onPress={() => Linking.openURL(grant.website)}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                    }}
                  >
                    <Globe
                      size={15}
                      color={EMBER.colors.textMid}
                      strokeWidth={1.8}
                    />
                  </IconButton>
                </View>
                <CardTitleText
                  style={{
                    fontSize: 22,
                    lineHeight: 28,
                    fontWeight: "600",
                    color: EMBER.colors.textMid,
                    textAlign: "center",
                  }}
                >
                  {grant.awardAmount}
                </CardTitleText>
              </View>

              <View style={{ gap: 14 }}>
                <DetailGrid minColumnWidth={88}>
                  <MoonDetailRow
                    icon={Calendar}
                    label="Deadline"
                    value={grant.applicationDeadline ?? "No fixed deadline"}
                  />
                  <MoonDetailRow
                    icon={Clock}
                    label="Frequency"
                    value={grant.frequency}
                  />
                  <MoonDetailRow
                    icon={DollarSign}
                    label="Application Fee"
                    value={
                      grant.applicationFee != null
                        ? `$${grant.applicationFee}`
                        : "Free"
                    }
                  />
                </DetailGrid>
              </View>
            </View>
          </InsetGroup>

          {/* Treatments Covered */}
          <Section title="Treatments covered" icon={Heart}>
            <TagList items={grant.treatmentsCovered} />
          </Section>

          {/* How to Apply */}
          <Section title="How to apply" icon={FileText}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                color: EMBER.colors.textMid,
                lineHeight: 22,
              }}
            >
              {grant.howToApply}
            </Text>
          </Section>

          {/* Eligibility */}
          <Section title="Eligibility" icon={Shield}>
            <DetailGrid minColumnWidth={128}>
              <MoonDetailRow
                icon={MapPin}
                label="Location"
                value={locationLabel}
              />
              {ageLabel && (
                <MoonDetailRow icon={Users} label="Age" value={ageLabel} />
              )}
              <MoonDetailRow
                icon={Shield}
                label="Insurance"
                value={insuranceLabel}
              />
              <MoonDetailRow
                icon={Globe}
                label="Citizenship"
                value={citizenshipLabel}
              />
              {maritalLabel && (
                <MoonDetailRow
                  icon={Heart}
                  label="Marital Status"
                  value={maritalLabel}
                />
              )}
              {diagnosisLabel && (
                <MoonDetailRow
                  icon={FileText}
                  label="Diagnosis"
                  value={diagnosisLabel}
                />
              )}
              {specialGroupLabel && (
                <MoonDetailRow
                  icon={Users}
                  label="Special Group"
                  value={specialGroupLabel}
                />
              )}
              {grant.eligibility.incomeMax != null && (
                <MoonDetailRow
                  icon={DollarSign}
                  label="Household income"
                  value={`Household income under $${grant.eligibility.incomeMax.toLocaleString()}`}
                />
              )}
            </DetailGrid>
          </Section>

          {/* Requirements */}
          <Section title="Requirements" icon={CheckCircle}>
            <View style={{ gap: 8 }}>
              {grant.requirements.map((req) => (
                <View
                  key={req}
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <CheckCircle
                    size={14}
                    color={EMBER.colors.green}
                    strokeWidth={2}
                    style={{ marginTop: 2 }}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      color: EMBER.colors.textMid,
                      lineHeight: 20,
                      flex: 1,
                    }}
                  >
                    {req}
                  </Text>
                </View>
              ))}
            </View>
          </Section>

          {/* About */}
          <Section title="About" icon={Info}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                color: EMBER.colors.textMid,
                lineHeight: 22,
              }}
            >
              {grant.longDescription}
            </Text>
          </Section>

          {/* Actions */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              marginTop: 8,
            }}
          >
            <VelvetPrimaryButton
              flex={1.2}
              fullWidth={false}
              label="Visit website"
              onPress={() => Linking.openURL(grant.website)}
              icon={<Globe size={16} color="#FFFFFF" strokeWidth={2} />}
            />
            <SoftSecondaryButton
              flex={1}
              label="Chat about this"
              onPress={() =>
                rootNavigation?.navigate("Chat", {
                  screen: "Assistant",
                  params: {
                    initialPrompt: buildGrantChatPrompt(grant),
                    autoSendInitialPrompt: true,
                  },
                })
              }
              leading={
                <MessageCircle
                  size={16}
                  color={EMBER.colors.textMid}
                  strokeWidth={1.8}
                />
              }
            />
          </View>
        </View>
      </ScrollView>
    </GlassScreenLayout>
  );
}
